import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import offlineDB from "../indexedDB/offlineDB";
import { slimPrinterSettings } from "./printerLookup";

export function isMevPrinterSetting(printer) {
  const apikey = printer?.generalTab?.apikey || printer?.apikey;
  return apikey === "MEV-RECEIPT-PRINTER" || printer?.id === "mev-receipt-printer";
}

export function isKitchenAllPrinterSetting(printer) {
  const apikey = printer?.generalTab?.apikey || printer?.apikey;
  return apikey === "KITCHEN-ALL" || printer?.id === "kitchen-all";
}

export function createKitchenAllPrinter() {
  return {
    id: "kitchen-all",
    generalTab: {
      name: "Kitchen All",
      apikey: "KITCHEN-ALL",
      autoPrint: true,
      autoPrintAfterConfirmation: false,
      printLanguage: "English",
      disable: false,
      email: "",
    },
    settingsTab: {},
    itemsTab: {
      kitchenSlip: true,
      cutbyCategory: false,
      cutbyItem: false,
      checkedItems: [],
      allItems: true,
    },
    hfTab: {},
    cloudPrintTab: { isEnabled: false },
    updated: new Date().toISOString(),
  };
}

export function createMevPrinterSetting() {
  return {
    id: "mev-receipt-printer",
    generalTab: {
      name: "Cloud MEV Receipt Printer",
      apikey: "MEV-RECEIPT-PRINTER",
      autoPrint: true,
      autoPrintAfterConfirmation: false,
      printLanguage: "English",
      disable: false,
      email: "",
    },
    settingsTab: {},
    itemsTab: { posReport: true },
    hfTab: {},
    updated: new Date().toISOString(),
  };
}

export function withBuiltinPrinters(printerSettings) {
  const list = Array.isArray(printerSettings) ? [...printerSettings] : [];
  let changed = false;
  const mevIndex = list.findIndex(isMevPrinterSetting);
  const mev = mevIndex >= 0 ? list[mevIndex] : createMevPrinterSetting();
  if (mevIndex < 0) changed = true;
  let kitchen = list.find(isKitchenAllPrinterSetting);
  if (!kitchen) {
    kitchen = createKitchenAllPrinter();
    changed = true;
  }
  const rest = list.filter((printer, index) => index !== mevIndex && !isKitchenAllPrinterSetting(printer));
  const next = [mev, kitchen, ...rest];
  if (!changed && next.some((printer, index) => printer !== list[index])) changed = true;
  return { list: next, changed };
}

export async function saveKitchenAllSetting(storeId, printer) {
  const id = String(storeId || "").trim();
  if (!id) return [];
  const ref = doc(db, "stores", id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return [];
  const current = Array.isArray(snap.data()?.printerSettings) ? snap.data().printerSettings : [];
  const { list } = withBuiltinPrinters(current);
  const next = list.map((entry) => (
    isKitchenAllPrinterSetting(entry)
      ? { ...entry, ...printer, id: entry.id || "kitchen-all" }
      : entry
  ));
  const printerSettings = slimPrinterSettings(next);
  await updateDoc(ref, { printerSettings });
  try {
    const existing = (await offlineDB.getMasterData("stores", id)) || { id };
    await offlineDB.saveMasterData("stores", id, { ...existing, id, printerSettings });
  } catch {
    // Firestore already has Kitchen All beside the MEV printer.
  }
  return printerSettings;
}

/** Save the MEV printer and Kitchen All on the store when either one is missing. */
export async function ensureBuiltinPrinters(storeId) {
  const id = String(storeId || "").trim();
  if (!id) return [];
  const ref = doc(db, "stores", id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return [];
  const current = Array.isArray(snap.data()?.printerSettings) ? snap.data().printerSettings : [];
  const { list, changed } = withBuiltinPrinters(current);
  if (!changed) return list;
  const printerSettings = slimPrinterSettings(list);
  await updateDoc(ref, { printerSettings });
  try {
    const existing = (await offlineDB.getMasterData("stores", id)) || { id };
    await offlineDB.saveMasterData("stores", id, { ...existing, id, printerSettings });
  } catch {
    // Firestore already has the two printers.
  }
  return printerSettings;
}

export async function setPrinterSettingDisabled(storeId, apiKey, disabled) {
  const id = String(storeId || "").trim();
  const key = String(apiKey || "").trim();
  if (!id || !key) return;
  const ref = doc(db, "stores", id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const current = Array.isArray(snap.data()?.printerSettings) ? snap.data().printerSettings : [];
  let changed = false;
  const next = current.map((entry) => {
    const entryKey = entry?.generalTab?.apikey || entry?.apikey;
    const matches = entryKey === key || (key === "KITCHEN-ALL" && isKitchenAllPrinterSetting(entry));
    if (!matches) return entry;
    changed = true;
    return {
      ...entry,
      generalTab: { ...(entry.generalTab || {}), disable: disabled === true },
    };
  });
  if (!changed) return;
  const printerSettings = slimPrinterSettings(next);
  await updateDoc(ref, { printerSettings });
  try {
    const existing = (await offlineDB.getMasterData("stores", id)) || { id };
    await offlineDB.saveMasterData("stores", id, { ...existing, id, printerSettings });
  } catch {
    // Firestore already has the printer setting.
  }
}
